import { useEffect, useState } from 'react';
import { camelToTitleCase } from '../../lib/formatters';
import { useFetch } from '../useFetch';
import { useOperator } from '../../contexts/OperatorContext';
import { BASE_URL_MAP } from '../../lib/environment';

type DeviceState = {
  deviceId: string;
  location: string;
  macAddress: string;
  state: string;
};

type HosDeviceState = {
  roomNumber: string;
} & DeviceState;

type ResDeviceState = {
  accountNumber: string;
} & DeviceState;

type ApiDeviceState<T> = {
  [K in keyof T & string as Capitalize<K>]: T[K];
};

type HosApiDeviceState = ApiDeviceState<HosDeviceState>;
type ResApiDeviceState = ApiDeviceState<ResDeviceState>;

export const useDeviceStates = (type: DashboardDeviceType) => {
  const [deviceStates, setDeviceStates] = useState<DeviceState[]>([]);
  const { makeRequest, loading, response } = useFetch();
  const operator = useOperator();
  const isRes = operator?.type === 'Residential' ?? false;

  useEffect(() => {
    if (response) {
      if (type.toLowerCase() === 'modem')
        setDeviceStates(
          response.map((r: HosApiDeviceState) => mapModemStates(r))
        );
      else if (type.toLowerCase() === 'stb')
        setDeviceStates(
          response.map((r: HosApiDeviceState | ResApiDeviceState) =>
            mapSTBState(r)
          )
        );
    }
  }, [response]);

  const getDeviceStates = () => {
    switch (type.toLowerCase()) {
      case 'stb':
        makeRequest(
          `${BASE_URL_MAP[operator.type]}/${operator?.id}${
            isRes ? '' : '/Dashboard'
          }/DeviceStateDetails`,
          'get'
        );
        break;
      case 'modem':
        makeRequest(
          `${BASE_URL_MAP['Hospitality']}/${operator?.id}/Dashboard/CableModemStatusDetails`,
          'get'
        );
        break;
    }
  };

  return { deviceStates, getDeviceStates, loading };
};

const convertModemStatus = (status: string) => {
  let result;
  switch (status.toLowerCase()) {
    case 'online(pt)':
      result = 'Online';
      break;
    case 'resetting':
    case 'init(o)':
      result = 'Rebooting';
      break;
    case 'init(d)':
      result = 'DHCP Received';
      break;
    case 'init(t)':
      result = 'ToD Started';
      break;
    case 'init(r1)':
    case 'init(r2)':
    case 'init(rc)':
      result = 'Ranging Mode';
      break;

    default:
      result = status;
  }
  return camelToTitleCase(result);
};

const mapModemStates = (deviceState: HosApiDeviceState): HosDeviceState => ({
  deviceId: deviceState.DeviceId,
  location: deviceState.Location,
  macAddress: deviceState.MacAddress,
  roomNumber: deviceState.RoomNumber,
  state: convertModemStatus(deviceState.State),
});

const mapSTBState = (
  deviceState: HosApiDeviceState | ResApiDeviceState
): HosDeviceState | ResDeviceState => {
  if ('RoomNumber' in deviceState) {
    return {
      deviceId: deviceState.DeviceId,
      location: deviceState.Location,
      macAddress: deviceState.MacAddress,
      roomNumber: deviceState.RoomNumber,
      state: deviceState.State,
    };
  } else {
    return {
      accountNumber: deviceState.AccountNumber,
      deviceId: deviceState.DeviceId,
      location: deviceState.Location,
      macAddress: deviceState.MacAddress,
      state: deviceState.State,
    };
  }
};
